import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
} from '@saas-pages/shared/public/runtime'
import {
  usePdfPageThumbnails,
  type PdfPageThumbnail,
  type PdfPreviewState,
} from '@/features/pdf-page-preview'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import {
  GAP_OPTIONS_MM,
  MARGIN_OPTIONS_MM,
  MAX_FILE_MB,
  MAX_SOURCE_PAGES,
  NUpPdfError,
  ORIENTATIONS,
  PAGE_ORDERS,
  PAGES_PER_SHEET,
  PAPER_SIZES,
  buildLayoutPreset,
  getGrid,
  getSheetDimensions,
  nUpFilename,
  pageCountBucket,
  validateInputSize,
  type GapMm,
  type MarginMm,
  type NUpOptions,
  type Orientation,
  type PageOrder,
  type PagesPerSheet,
  type PaperSize,
} from './lib'
import { exportNUpPdf } from './export'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const BASE_ANALYTICS = {
  app: 'pdfy',
  domain: 'pdfy.ru',
  route: '/neskolko-stranits-pdf-na-liste',
  utility: 'n-up-pdf',
  processingMode: 'local',
} as const satisfies ToolAnalyticsContext

const DEFAULT_OPTIONS: NUpOptions = {
  pagesPerSheet: 4,
  paperSize: 'a4',
  orientation: 'portrait',
  marginMm: 10,
  gapMm: 5,
  pageOrder: 'across',
}

interface Result {
  blob: Blob
  objectUrl: string
  outputSheets: number
}

interface NeskolkoStranitsPdfFormProps {
  exportOverride?: typeof exportNUpPdf
  previewOverride?: PdfPreviewState
}

export function NeskolkoStranitsPdfForm({
  exportOverride = exportNUpPdf,
  previewOverride,
}: NeskolkoStranitsPdfFormProps = {}) {
  const t = useT()
  const formatBytes = useFormatBytes()
  const [file, setFile] = useState<File | null>(null)
  const [options, setOptions] = useState<NUpOptions>(DEFAULT_OPTIONS)
  const [totalPages, setTotalPages] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const mountedRef = useRef(false)
  const resultUrlRef = useRef<string | null>(null)
  const activeControllerRef = useRef<AbortController | null>(null)
  const activeRequestRef = useRef(0)
  const inFlightRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      activeRequestRef.current += 1
      inFlightRef.current = false
      activeControllerRef.current?.abort()
      activeControllerRef.current = null
      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
      resultUrlRef.current = null
    }
  }, [])

  function clearResult() {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    resultUrlRef.current = null
    setResult(null)
  }

  function updateOptions(update: (current: NUpOptions) => NUpOptions) {
    clearResult()
    setOptions(update)
  }

  function selectFile(nextFile: File) {
    clearResult()
    setError(null)
    setTotalPages(null)
    try {
      validateInputSize(nextFile.size)
      setFile(nextFile)
    } catch {
      setFile(null)
      setError(format(t.common.tooLarge, { size: MAX_FILE_MB }))
      trackToolRejection(BASE_ANALYTICS, 'size_limit')
    }
  }

  function reset() {
    clearResult()
    setFile(null)
    setTotalPages(null)
    setError(null)
    setOptions(DEFAULT_OPTIONS)
  }

  async function exportPdf() {
    if (
      inFlightRef.current
      || !file
      || totalPages === null
      || totalPages <= 0
      || totalPages > MAX_SOURCE_PAGES
    ) return

    inFlightRef.current = true
    const requestId = ++activeRequestRef.current
    const controller = new AbortController()
    activeControllerRef.current = controller
    clearResult()
    setLoading(true)
    setError(null)
    const startedAt = performance.now()
    const analytics: ToolAnalyticsContext = {
      ...BASE_ANALYTICS,
      layoutPreset: buildLayoutPreset(options),
      pageCountBucket: pageCountBucket(totalPages),
    }
    const operation = startToolOperation(analytics)

    try {
      const output = await exportOverride(file, options, controller.signal)
      if (
        controller.signal.aborted
        || !mountedRef.current
        || requestId !== activeRequestRef.current
      ) return

      const blob = new Blob([output.bytes], { type: 'application/pdf' })
      const objectUrl = URL.createObjectURL(blob)
      if (
        controller.signal.aborted
        || !mountedRef.current
        || requestId !== activeRequestRef.current
      ) {
        URL.revokeObjectURL(objectUrl)
        return
      }
      resultUrlRef.current = objectUrl
      setResult({ blob, objectUrl, outputSheets: output.outputSheets })
      operation.success(performance.now() - startedAt)
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        operation.error('cancelled')
        return
      }
      const { message, code } = errorDetails(cause, t.nUpPdf.errors)
      if (mountedRef.current && requestId === activeRequestRef.current) setError(message)
      operation.error(code)
    } finally {
      if (requestId === activeRequestRef.current) {
        inFlightRef.current = false
        activeControllerRef.current = null
        if (mountedRef.current) setLoading(false)
      }
    }
  }

  const dimensions = getSheetDimensions(options.paperSize, options.orientation)
  const tooManyPages = totalPages !== null && totalPages > MAX_SOURCE_PAGES
  const emptyDocument = totalPages !== null && totalPages <= 0

  return (
    <div className="space-y-6">
      {!file ? (
        <PdfDropZone onFile={selectFile} />
      ) : (
        <>
          <Card>
            <CardContent className="space-y-5 p-5">
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-accent/20 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(file.size)}
                    {totalPages === null ? '' : ` · ${format(t.nUpPdf.sourcePages, { count: totalPages })}`}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={reset} disabled={loading}>
                  {t.nUpPdf.replaceFile}
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <OptionSelect
                  label={t.nUpPdf.pagesPerSheet}
                  value={String(options.pagesPerSheet)}
                  disabled={loading}
                  onChange={(value) => updateOptions((current) => ({
                    ...current,
                    pagesPerSheet: Number(value) as PagesPerSheet,
                  }))}
                  options={PAGES_PER_SHEET.map((value) => ({
                    value: String(value),
                    label: format(t.nUpPdf.pagesPreset, { count: value }),
                  }))}
                />
                <OptionSelect
                  label={t.nUpPdf.paperSize}
                  value={options.paperSize}
                  disabled={loading}
                  onChange={(value) => updateOptions((current) => ({ ...current, paperSize: value as PaperSize }))}
                  options={PAPER_SIZES.map((value) => ({ value, label: value.toUpperCase() }))}
                />
                <OptionSelect
                  label={t.nUpPdf.orientation}
                  value={options.orientation}
                  disabled={loading}
                  onChange={(value) => updateOptions((current) => ({ ...current, orientation: value as Orientation }))}
                  options={ORIENTATIONS.map((value) => ({
                    value,
                    label: value === 'portrait' ? t.nUpPdf.portrait : t.nUpPdf.landscape,
                  }))}
                />
                <OptionSelect
                  label={t.nUpPdf.margin}
                  value={String(options.marginMm)}
                  disabled={loading}
                  onChange={(value) => updateOptions((current) => ({ ...current, marginMm: Number(value) as MarginMm }))}
                  options={MARGIN_OPTIONS_MM.map((value) => ({ value: String(value), label: `${value} мм` }))}
                />
                <OptionSelect
                  label={t.nUpPdf.gap}
                  value={String(options.gapMm)}
                  disabled={loading}
                  onChange={(value) => updateOptions((current) => ({ ...current, gapMm: Number(value) as GapMm }))}
                  options={GAP_OPTIONS_MM.map((value) => ({ value: String(value), label: `${value} мм` }))}
                />
                <OptionSelect
                  label={t.nUpPdf.pageOrder}
                  value={options.pageOrder}
                  disabled={loading}
                  onChange={(value) => updateOptions((current) => ({ ...current, pageOrder: value as PageOrder }))}
                  options={PAGE_ORDERS.map((value) => ({
                    value,
                    label: value === 'across' ? t.nUpPdf.across : t.nUpPdf.down,
                  }))}
                />
              </div>

              <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                {format(t.nUpPdf.dimensions, {
                  width: dimensions.widthMm,
                  height: dimensions.heightMm,
                })}
              </p>
            </CardContent>
          </Card>

          <SheetPreview
            file={file}
            options={options}
            totalPages={totalPages}
            onDocumentLoad={(count) => {
              setTotalPages(count)
              if (count <= 0) setError(t.nUpPdf.errors.emptyDocument)
            }}
            previewOverride={previewOverride}
          />

          {tooManyPages && (
            <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {format(t.nUpPdf.errors.tooManyPages, { count: MAX_SOURCE_PAGES })}
            </p>
          )}

          {emptyDocument && !error && (
            <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {t.nUpPdf.errors.emptyDocument}
            </p>
          )}

          {error && (
            <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </p>
          )}

          <Button
            size="lg"
            className="w-full"
            disabled={loading || totalPages === null || emptyDocument || tooManyPages}
            onClick={() => void exportPdf()}
          >
            {loading ? t.nUpPdf.loading : t.nUpPdf.submit}
          </Button>
        </>
      )}

      {result && file && (
        <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
          <CardContent className="space-y-4 p-5">
            <RevealOnMount />
            <div>
              <p className="font-semibold text-green-700 dark:text-green-200">{t.common.done}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {format(t.nUpPdf.resultSummary, {
                  sheets: result.outputSheets,
                  size: formatBytes(result.blob.size),
                })}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button asChild className="flex-1">
                <a href={result.objectUrl} download={nUpFilename(file.name)}>
                  {t.common.download}
                </a>
              </Button>
              <Button variant="outline" onClick={reset}>{t.nUpPdf.again}</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

interface PdfDropZoneProps {
  onFile: (file: File) => void
}

function PdfDropZone({ onFile }: PdfDropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    const file = files?.[0]
    if (file && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))) {
      onFile(file)
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t.common.pickPdf}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click()
      }}
      onDragOver={(event) => {
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault()
        setDragging(false)
        handleFiles(event.dataTransfer.files)
      }}
      className={cn(
        'cursor-pointer rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
        dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-accent/30',
      )}
    >
      <p className="font-medium">
        <FilePickPrompt drag={t.common.dragPdf} />
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        {format(t.common.pdfLimit, { size: MAX_FILE_MB })} · {format(t.nUpPdf.pageLimit, { count: MAX_SOURCE_PAGES })}
      </p>
      <p className="mt-2 text-xs text-muted-foreground">{t.common.localBadge}</p>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        onChange={(event) => handleFiles(event.target.files)}
      />
    </div>
  )
}

interface OptionSelectProps {
  label: string
  value: string
  disabled: boolean
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}

function OptionSelect({ label, value, disabled, options, onChange }: OptionSelectProps) {
  return (
    <label className="space-y-1.5 text-sm font-medium">
      <span>{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  )
}

interface SheetPreviewProps {
  file: File
  options: NUpOptions
  totalPages: number | null
  onDocumentLoad: (totalPages: number) => void
  previewOverride?: PdfPreviewState
}

function SheetPreview({
  file,
  options,
  totalPages,
  onDocumentLoad,
  previewOverride,
}: SheetPreviewProps) {
  const t = useT()
  const maxSheets = 4
  const maxPages = options.pagesPerSheet * maxSheets
  const loadedPreview = usePdfPageThumbnails({ file, maxPages, targetWidth: 150, onDocumentLoad })
  const preview = previewOverride ?? loadedPreview
  useEffect(() => {
    if (previewOverride?.totalPages !== null && previewOverride?.totalPages !== undefined) {
      onDocumentLoad(previewOverride.totalPages)
    }
  }, [onDocumentLoad, previewOverride?.totalPages])
  const dimensions = getSheetDimensions(options.paperSize, options.orientation)
  const { columns, rows } = getGrid(options.pagesPerSheet, options.orientation)
  const sheets = useMemo(
    () => buildPreviewSheets(preview.thumbnails, options, maxSheets),
    [options, preview.thumbnails],
  )
  const sheetCount = totalPages === null ? null : Math.ceil(totalPages / options.pagesPerSheet)

  return (
    <Card className="border-primary/15 bg-primary/5">
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">{t.nUpPdf.previewTitle}</h2>
            <p className="text-xs text-muted-foreground">
              {format(t.nUpPdf.dimensions, { width: dimensions.widthMm, height: dimensions.heightMm })}
            </p>
          </div>
          {sheetCount !== null && (
            <span className="text-xs font-medium text-muted-foreground">
              {format(t.nUpPdf.outputSheets, { count: sheetCount })}
            </span>
          )}
        </div>

        {preview.status === 'error' ? (
          <p className="rounded-md border border-amber-300/40 bg-amber-100/40 p-3 text-xs">
            {t.nUpPdf.previewError}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {sheets.map((sheet, sheetIndex) => (
              <div key={sheetIndex}>
                <div
                  className="grid overflow-hidden rounded border bg-white shadow-sm"
                  style={{
                    aspectRatio: `${dimensions.widthMm} / ${dimensions.heightMm}`,
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
                    padding: `${Math.max(2, options.marginMm / 2)}px`,
                    gap: `${Math.max(1, options.gapMm / 2)}px`,
                  }}
                >
                  {sheet.map((thumbnail, slotIndex) => (
                    <div key={slotIndex} className="flex min-h-0 items-center justify-center overflow-hidden border border-dashed border-slate-200">
                      {thumbnail && (
                        <img
                          src={thumbnail.dataUrl}
                          alt={format(t.pdfPreview.pageAlt, { page: thumbnail.pageNumber })}
                          className="max-h-full max-w-full object-contain"
                        />
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-center text-[11px] text-muted-foreground">
                  {format(t.nUpPdf.sheetLabel, { count: sheetIndex + 1 })}
                </p>
              </div>
            ))}
            {preview.status === 'loading' && sheets.length === 0 && (
              <div className="aspect-[3/4] animate-pulse rounded border bg-background" />
            )}
          </div>
        )}

        {sheetCount !== null && sheetCount > maxSheets && (
          <p className="text-xs text-muted-foreground">
            {format(t.nUpPdf.previewLimited, { shown: maxSheets, total: sheetCount })}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function buildPreviewSheets(
  thumbnails: readonly PdfPageThumbnail[],
  options: NUpOptions,
  maxSheets: number,
): (Array<PdfPageThumbnail | null>)[] {
  const { columns } = getGrid(options.pagesPerSheet, options.orientation)
  const sheetCount = Math.min(Math.ceil(thumbnails.length / options.pagesPerSheet), maxSheets)

  return Array.from({ length: sheetCount }, (_, sheetIndex) => {
    const slots: Array<PdfPageThumbnail | null> = Array.from({ length: options.pagesPerSheet }, () => null)
    const start = sheetIndex * options.pagesPerSheet
    thumbnails.slice(start, start + options.pagesPerSheet).forEach((thumbnail, index) => {
      const sourcePageIndex = start + index
      const row = options.pageOrder === 'across'
        ? Math.floor(index / columns)
        : index % getGrid(options.pagesPerSheet, options.orientation).rows
      const column = options.pageOrder === 'across'
        ? index % columns
        : Math.floor(index / getGrid(options.pagesPerSheet, options.orientation).rows)
      slots[row * columns + column] = { ...thumbnail, pageNumber: sourcePageIndex + 1 }
    })
    return slots
  })
}

function errorDetails(
  cause: unknown,
  errors: ReturnType<typeof useT>['nUpPdf']['errors'],
): { message: string; code: ToolErrorCode } {
  if (!(cause instanceof NUpPdfError)) return { message: errors.parseError, code: 'processing_failed' }
  if (cause.code === 'too_large') return { message: errors.tooLarge, code: 'size_limit' }
  if (cause.code === 'too_many_pages' || cause.code === 'too_many_sheets') {
    return { message: format(errors.tooManyPages, { count: MAX_SOURCE_PAGES }), code: 'size_limit' }
  }
  if (cause.code === 'encrypted') return { message: errors.encrypted, code: 'unsupported_input' }
  if (cause.code === 'invalid_input') return { message: errors.emptyDocument, code: 'invalid_input' }
  if (cause.code === 'invalid_layout') return { message: errors.invalidLayout, code: 'invalid_input' }
  return { message: errors.parseError, code: 'processing_failed' }
}
